/**
 * Visual-novel-content (§5).
 *
 * Gegeven in het spec en hier 1-op-1 overgenomen: De Gids' openingsmonoloog
 * (§5.1), de 3 universele houdingen + bonussen + per-chakra reactieregels (§5.2),
 * en de reflectievragen per eindbaas (§5.3).
 *
 * NOG TE SCHRIJVEN (fase 3, VN-laag — zelf, ter review): de 6 brugteksten tussen
 * de chakra's (§5.1) en de reflectie-LOGREGELS per antwoordoptie (§5.3). Die
 * staan hieronder als lege/gemarkeerde placeholders.
 */
import type { Origin } from './types';

/* ------------------------------------------------------------------ */
/* §5.1 De Gids                                                        */
/* ------------------------------------------------------------------ */

export const GIDS_OPENING =
  'Er zijn zeven wielen in jou die altijd al draaiden — soms vrij, soms vast. Dit is geen gevecht tegen iets buiten jezelf. Het is een terugreis naar wat er al was, voordat het werd dichtgemetseld. Ik loop met je mee. Niet om het voor je op te lossen — maar om je te herinneren wat je al wist.';

/**
 * Brugteksten tussen chakra's (1→2 … 6→7), max ~60 woorden, voortbouwend op de
 * Wereld-teksten. NOG TE SCHRIJVEN (fase 3). Index 0 = brug 1→2, … index 5 = 6→7.
 * Eén voorbeeld uit het spec is alvast ingevuld (brug 1→2).
 */
export const GIDS_BRUGGEN: (string | null)[] = [
  'Je staat weer rechtop. Maar rechtop staan is niet hetzelfde als voelen. Wat in Muladhara stabiel werd, mag nu gaan stromen.',
  'Je voelt weer — de rivier stroomt. Maar voelen zonder richting verdrinkt. Wat in Svadhisthana loskwam, vraagt nu om vuur: een wil die kiest, een kracht die draagt wat je voelt.',
  'Je vuur brandt weer, je wil is van jou. Maar kracht die alleen voor zichzelf brandt, verschroeit. Laat Manipura\'s vlam nu zachter worden, tot warmte — en open het hart dat ze beschermt.',
  'Je hart staat op een kier. Wat je daar voelt, wil gezegd worden. Wat in Anahata opende, zoekt nu een stem: de moed om je waarheid hoorbaar te maken.',
  'Je stem klinkt weer. Maar spreken zonder zien wordt herhalen. Laat Vishuddha\'s helderheid nu naar binnen keren, naar het oog dat waarneemt voorbij de woorden.',
  'Je ziet weer helder. En heldere ogen merken: je bent niet los. Wat Ajna onthulde, opent de laatste poort — niet naar méér inzicht, maar naar verbinding met het geheel.',
];

/* ------------------------------------------------------------------ */
/* §5.2 Confrontatie — houdingen                                       */
/* ------------------------------------------------------------------ */

export type Houding = 'erkennen' | 'bevragen' | 'confronteren';

export const HOUDINGEN: Houding[] = ['erkennen', 'bevragen', 'confronteren'];

export const HOUDING_LABEL: Record<Houding, string> = {
  erkennen: 'Erkennen',
  bevragen: 'Bevragen',
  confronteren: 'Confronteren',
};

/** Mechanische bonus (geldt 1e beurt van het gevecht). */
export interface HoudingBonus {
  startBlock?: number;
  extraDraw?: number;
  eersteAanvalMin?: number;
}

export const HOUDING_BONUS: Record<Houding, HoudingBonus> = {
  erkennen: { startBlock: 3 },
  bevragen: { extraDraw: 2 },
  confronteren: { eersteAanvalMin: 2 },
};

/**
 * Per-chakra reactieregel van de baas, per houding (§5.2). Generiek toepasbaar
 * op alle 4 bazen van die chakra.
 */
export const CONFRONTATIE_REACTIES: Record<Origin, Record<Houding, string>> = {
  1: {
    erkennen: 'Erken me, en ik word alleen maar zwaarder.',
    bevragen: 'Vragen stellen helpt je niet als de grond onder je wankelt.',
    confronteren: 'Vecht maar. Angst kent geen vermoeidheid.',
  },
  2: {
    erkennen: 'Voel het maar. Het zal je overspoelen.',
    bevragen: 'Waarom vragen naar iets dat je toch niet mag voelen?',
    confronteren: 'Verzet bevriest sneller dan overgave.',
  },
  3: {
    erkennen: 'Erkennen is geen kracht. Het is overgave.',
    bevragen: 'Waarom twijfel je nog steeds aan jezelf?',
    confronteren: 'Eindelijk. Laat zien wat er nog over is van je wil.',
  },
  4: {
    erkennen: 'Erken de pijn, en hij verankert zich dieper.',
    bevragen: 'Waarom zou je nog vragen om iets te voelen?',
    confronteren: 'Een gesloten hart kun je niet bevechten. Alleen openen.',
  },
  5: {
    erkennen: 'Erkennen in stilte telt niet als spreken.',
    bevragen: 'Een goede vraag. Jammer dat je hem nooit luid zal stellen.',
    confronteren: 'Eindelijk een stem. We zullen zien hoelang die standhoudt.',
  },
  6: {
    erkennen: 'Erkennen is nog geen zien.',
    bevragen: 'Vraag zoveel je wil. Ik geef je net zo lief een leugen als een waarheid.',
    confronteren: 'Confronteer de sluier, en ik leg er nog een neer.',
  },
  7: {
    erkennen: 'Erken de afstand, en ze wordt voelbaar groter.',
    bevragen: 'Vragen veronderstellen dat er iemand is om te antwoorden.',
    confronteren: 'Je kunt niet vechten tegen afwezigheid.',
  },
};

/* ------------------------------------------------------------------ */
/* §5.3 Reflectievragen (alleen na eindbazen)                          */
/* ------------------------------------------------------------------ */

export interface ReflectieOptie {
  /** Het antwoord zoals de speler het kiest. */
  antwoord: string;
  /** Logregel voor het Reisdagboek (flavor). NOG TE SCHRIJVEN (fase 3). */
  logregel: string | null;
}

export interface ReflectieVraag {
  chakra: Origin;
  eindbaasId: string;
  vraag: string;
  opties: ReflectieOptie[];
}

/** Geïndexeerd op chakra-nummer; antwoorden uit §5.3, logregels nog te schrijven. */
export const REFLECTIES: Record<Origin, ReflectieVraag> = {
  1: {
    chakra: 1,
    eindbaasId: 'ch1-kharos',
    vraag: 'Wat heb je vandaag geleerd over je eigen fundament?',
    opties: [
      { antwoord: 'Dat ik steviger sta dan ik dacht', logregel: 'Je merkte dat de grond je droeg, ook toen je trilde. Dat vertrouwen neem je mee.' },
      { antwoord: 'Dat overleven niet hetzelfde is als leven', logregel: 'Je zag het verschil tussen volhouden en werkelijk aanwezig zijn. Vanaf hier kies je voor het laatste.' },
      { antwoord: 'Dat rust verdienen geen zwakte is', logregel: 'Je gaf jezelf toestemming om te rusten zonder schuld. Dat is geen luiheid, dat is wortelen.' },
    ],
  },
  2: {
    chakra: 2,
    eindbaasId: 'ch2-morveth',
    vraag: 'Wat doe je de volgende keer dat je iets voelt opkomen?',
    opties: [
      { antwoord: 'Ik laat het er even zijn', logregel: 'Je leerde een gevoel niet meteen weg te duwen. Ruimte geven is al half ontdooien.' },
      { antwoord: 'Ik zoek een creatieve uitlaatklep', logregel: 'Je liet emotie stromen in vorm — dans, woord, beeld. Wat beweegt, bevriest niet.' },
      { antwoord: 'Ik deel het met iemand', logregel: 'Je koos om niet alleen te voelen. Gedeeld verdriet wordt lichter, gedeelde vreugde groter.' },
    ],
  },
  3: {
    chakra: 3,
    eindbaasId: 'ch3-gravuun',
    vraag: 'Waar leg jij vanaf nu je grens?',
    opties: [
      { antwoord: 'Bij wat mij uitput', logregel: 'Je grens ligt waar jouw energie eindigt. "Nee" tegen uitputting is "ja" tegen jezelf.' },
      { antwoord: 'Bij wie mij klein maakt', logregel: 'Je beschermt voortaan je eigen vuur tegen wie het probeert te doven.' },
      { antwoord: 'Bij mezelf, als ik mezelf afbreek', logregel: 'De zwaarste grens: die tegen je eigen criticus. Je koos jouw kant.' },
    ],
  },
  4: {
    chakra: 4,
    eindbaasId: 'ch4-varuun',
    vraag: 'Wat doe je met een oud verdriet dat je nog draagt?',
    opties: [
      { antwoord: 'Ik geef het ruimte om gevoeld te worden', logregel: 'Je laat het verdriet bestaan zonder het te bestrijden. Gevoeld, verliest het zijn gewicht.' },
      { antwoord: 'Ik vergeef, niet voor de ander, voor mij', logregel: 'Je legt de last neer — niet om hen vrij te pleiten, maar om je eigen hart te bevrijden.' },
      { antwoord: 'Ik zoek opnieuw verbinding, al is het klein', logregel: 'Eén klein contact, één open moment: zo gaat een gesloten hart weer ademen.' },
    ],
  },
  5: {
    chakra: 5,
    eindbaasId: 'ch5-silvareth',
    vraag: 'Welke waarheid mag jij voortaan harder uitspreken?',
    opties: [
      { antwoord: 'Wat ik echt voel', logregel: 'Je gevoel verdient een stem, ook als die trilt. Vanaf nu spreek je het uit.' },
      { antwoord: 'Wat ik echt wil', logregel: 'Je verlangen is geen schaamte maar richting. Je durft het voortaan te benoemen.' },
      { antwoord: 'Wie ik echt ben', logregel: 'Je stopt met je aanpassen aan andermans beeld. Je stem en je zelf vallen weer samen.' },
    ],
  },
  6: {
    chakra: 6,
    eindbaasId: 'ch6-mavoreth',
    vraag: 'Waar vertrouw je voortaan meer op?',
    opties: [
      { antwoord: 'Mijn intuïtie', logregel: 'Je leerde luisteren naar het weten dat ouder is dan woorden.' },
      { antwoord: 'Mijn stilte', logregel: 'In de stilte, niet in het lawaai van informatie, klinkt je eigen helderheid op.' },
      { antwoord: 'Mijn eigen onderzoek, niet andermans antwoorden', logregel: 'Je vertrouwt voortaan op wat je zélf onderzoekt, niet op wat je wordt opgelegd.' },
    ],
  },
  7: {
    chakra: 7,
    eindbaasId: 'ch7-theluun',
    vraag: 'Wat betekent ware verbinding voor jou, vanaf nu?',
    opties: [
      { antwoord: 'Aanwezig zijn, ook als het oncomfortabel is', logregel: 'Verbinding vraagt geen perfectie, alleen aanwezigheid — juist als het schuurt.' },
      { antwoord: 'Mezelf toestaan onderdeel te zijn van iets groters', logregel: 'Je hoeft niet boven het leven te staan; je mag er middenin horen.' },
      { antwoord: 'Elke dag opnieuw kiezen voor contact', logregel: 'Verbinding is geen eindpunt maar een dagelijkse keuze. Je begint vandaag.' },
    ],
  },
};
